import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons";
import "../css/Common.css";
import "./css/AlertsIntelligenceHub.css";

/**
 * Modal for sharing alerts with family members.
 *
 * SECURITY: This modal does NOT fetch or display group IDs, member UUIDs,
 * or any sensitive group information. It only sends the alert ID and an
 * optional message to the backend. The backend determines the user's
 * family group and members from the authenticated session.
 *
 * @param {boolean} isOpen - Modal open state
 * @param {Object} alert - Alert to share
 * @param {Function} onClose - Handler for closing modal
 * @param {Function} onShare - Handler for share action (receives alertId, options)
 */
function FamilySharingModal({ isOpen, alert, onClose, onShare }) {
  // State - simplified: only message input and UI states
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sharedWith, setSharedWith] = useState([]);
  const [warningMessage, setWarningMessage] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Reset state when modal opens/closes
   */
  useEffect(() => {
    if (isOpen) {
      setMessage("");
      setError(null);
      setSuccess(false);
      setSharedWith([]);
      setWarningMessage(null);
    }
  }, [isOpen]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Handle share button click.
   * Only sends alertId and optional message - backend handles everything else.
   */
  const handleShare = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call onShare with only alertId and message - no group IDs or member UUIDs
      const response = await onShare(alert.id, {
        message: message.trim() || null,
      });

      // Response contains recipient first names for feedback
      setSharedWith(response?.recipientNames || []);
      setWarningMessage(response?.warningMessage || null);
      setSuccess(true);

      // Close modal after delay to show success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error("[FamilySharingModal] Error sharing:", err);
      // Check for specific error messages from backend
      const errorMessage =
        err?.response?.data?.errorMessage ||
        err?.message ||
        "Failed to share alert. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  if (!isOpen) return null;

  return (
    <div
      className="aih-modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sharing-modal-title"
    >
      <div className="aih-modal">
        {/* Header */}
        <header className="aih-modal-header">
          <h2 id="sharing-modal-title" className="aih-modal-title">
            <span className="aih-modal-icon">
              <FontAwesomeIcon icon={faShareAlt} />
            </span>
            Share with Family
          </h2>
          <button
            className="aih-modal-close"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close modal"
          >
            ×
          </button>
        </header>

        {/* Content */}
        <div className="aih-modal-content">
          {/* Success state */}
          {success && (
            <div className="aih-modal-success">
              <span className="aih-success-icon">✓</span>
              <p>Alert shared with your family!</p>
              {sharedWith.length > 0 && (
                <p className="aih-success-detail">
                  Shared with {sharedWith.join(", ")}
                </p>
              )}
              {warningMessage && (
                <p className="aih-success-detail aih-warning-detail">
                  {warningMessage}
                </p>
              )}
            </div>
          )}

          {/* Normal state */}
          {!success && (
            <>
              {/* Alert preview */}
              {alert && (
                <div className="aih-share-alert-preview">
                  <span className="aih-preview-label">Sharing:</span>
                  <span className="aih-preview-title">{alert.title}</span>
                </div>
              )}

              {/* Confirmation message */}
              <div className="aih-share-confirmation">
                <p className="aih-share-info">
                  This alert will be shared with all members of your family
                  group. They will receive a notification about this security
                  alert.
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="aih-modal-error">
                  <span className="aih-error-icon">!</span>
                  {error}
                </div>
              )}

              {/* Personal message */}
              <div className="aih-form-group">
                <label className="aih-form-label">
                  Personal Message
                  <span className="aih-optional">(optional)</span>
                </label>
                <textarea
                  className="aih-message-input"
                  placeholder="Add a message to your family members..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
                <div className="aih-char-count">
                  {message.length}/500 characters
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <footer className="aih-modal-footer">
            <button
              className="aih-modal-cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="aih-modal-share-btn"
              onClick={handleShare}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="aih-spinner-tiny" />
                  Sharing...
                </>
              ) : (
                <>
                  <span className="aih-btn-icon">
                    <FontAwesomeIcon icon={faShareAlt} />
                  </span>{" "}
                  Share with Family
                </>
              )}
            </button>
          </footer>
        )}

        {/* Close button for success state */}
        {success && (
          <footer className="aih-modal-footer">
            <button className="aih-modal-close-btn" onClick={handleClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

export default FamilySharingModal;
